import * as React from 'react';

import {Avatar} from 'shells/avatar/avatar';
import {colors} from 'shells/colors/colors';
import {
    AccountsCircleIcon,
    ActivityEmptyStateIcon,
    ChatIcon,
    ConnectedFormsIcon,
    ErrorTriangleIcon,
    LinkClickIcon,
    NutshellLogoMarkIcon,
    SchedulerIcon,
    SmsIcon,
    StepIcon,
    TaskIcon,
    WebFormIcon,
    MessengerColoredIcon,
    InstagramColoredIcon,
    QuoteIcon,
    WhatsAppIcon,
    InvoiceIcon,
    MarketingAutomationIcon,
    NavIconEmailIcon,
} from 'shells/icon';
import {ReactionEmoji} from 'shells/reaction-emoji/reaction-emoji';

import type {GetNotifications_notifications as NotificationObject} from 'nutshell-graphql-types';

export function getAvatar(notification: NotificationObject): React.ReactElement<any> | null {
    const entity = notification.entity;

    if (
        notification.type === 'COMMENT' ||
        notification.type === 'MENTION' ||
        notification.type === 'ACTIVITY' ||
        notification.type === 'STEP' ||
        notification.type === 'TASK'
    ) {
        return (
            <Avatar
                size={30}
                avatarUrl={
                    notification.triggeringUser ? notification.triggeringUser.avatarUrl : undefined
                }
                initials={
                    notification.triggeringUser ? notification.triggeringUser.initials : undefined
                }
                type='users'
            />
        );
    } else if (notification.type === 'SUBMISSION') {
        // @ts-expect-error
        return <WebFormIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'GENERIC') {
        return <NutshellLogoMarkIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'NEW_CONNECTED_FORM') {
        return <ConnectedFormsIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'LINK_CLICKED') {
        return <LinkClickIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'ACTIVITY_REMINDER') {
        return (
            <ActivityEmptyStateIcon
                className='flex justify-center'
                size={28}
                fill={colors.reportNavy}
            />
        );
    } else if (notification.type === 'STEP_REMINDER') {
        return <StepIcon className='flex justify-center' size={28} fill={colors.reportNavy} />;
    } else if (notification.type === 'TASK_REMINDER') {
        return <TaskIcon className='flex justify-center' size={28} fill={colors.reportNavy} />;
    } else if (notification.type === 'EMAIL_AUTOMATION_STATUS') {
        return <NutshellLogoMarkIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'SCHEDULER_BOOKING') {
        return <SchedulerIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'BROKEN_INTEGRATION') {
        // @ts-expect-error
        return <ErrorTriangleIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'MEETING_NEEDS_ATTENTION') {
        // @ts-expect-error
        return <ErrorTriangleIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'WEBHOOK_DEACTIVATE') {
        // @ts-expect-error
        return <ErrorTriangleIcon size={30} fill={colors.sidebarPrimary} />;
    } else if (notification.type === 'ACCOUNT_ASSIGNMENT') {
        // @ts-expect-error
        return <AccountsCircleIcon fill={colors.plum} size={30} />;
    } else if (notification.type === 'QUOTE_VIEWED' || notification.type === 'QUOTE_ACCEPTED') {
        // @ts-expect-error
        return <QuoteIcon size={30} fill={colors.black} />;
    } else if (notification.type === 'INVOICE_VIEWED') {
        // @ts-expect-error
        return <InvoiceIcon size={30} fill={colors.black} />;
    } else if (notification.type === 'PAGE_VIEWED' || notification.type === 'PAGE_VIEWED_TEST') {
        // @ts-expect-error
        return <MarketingAutomationIcon size={30} color={colors.blue} />;
    }

    if (!entity) {
        return null;
    }

    if (entity.__typename === 'Lead') {
        return <Avatar size={30} avatarUrl={entity.avatarUrl} initials={undefined} type='leads' />;
    } else if (entity.__typename === 'Contact') {
        return (
            <Avatar size={30} avatarUrl={entity.avatarUrl} initials={undefined} type='contacts' />
        );
    } else if (entity.__typename === 'Reaction') {
        return (
            <div className='mt-8'>
                <ReactionEmoji size={30} reactionType={entity.reactionType} />
            </div>
        );
    }

    if (entity.__typename === 'Message' && notification.type === 'SMS_REPLY') {
        switch (entity.thread.threadType) {
            case 'SMS':
                // @ts-expect-error
                return <SmsIcon color={colors.orange} size={30} />;
            case 'CHAT':
                // @ts-expect-error
                return <ChatIcon size={30} fill={colors.orange} />;
            case 'MESSENGER':
                // @ts-expect-error
                return <MessengerColoredIcon size={30} fill={colors.orange} />;
            case 'INSTAGRAM':
                // @ts-expect-error
                return <InstagramColoredIcon size={30} fill={colors.orange} />;
            case 'WHATSAPP':
                // @ts-expect-error
                return <WhatsAppIcon size={30} fill={colors.whatsappGreen} />;
            case 'EMAIL':
                // @ts-expect-error
                return <NavIconEmailIcon size={30} fill={colors.orange} />;
            default:
                return null;
        }
    }

    if (
        notification.type === 'THREAD_UNASSIGNED_START' ||
        notification.type === 'THREAD_ASSIGNMENT'
    ) {
        // @ts-expect-error
        switch (entity.threadType) {
            case 'SMS':
                // @ts-expect-error
                return <SmsIcon color={colors.orange} size={30} />;
            case 'CHAT':
                // @ts-expect-error
                return <ChatIcon size={30} fill={colors.orange} />;
            case 'MESSENGER':
                // @ts-expect-error
                return <MessengerColoredIcon size={30} fill={colors.orange} />;
            case 'INSTAGRAM':
                // @ts-expect-error
                return <InstagramColoredIcon size={30} fill={colors.orange} />;
            case 'WHATSAPP':
                // @ts-expect-error
                return <WhatsAppIcon size={30} fill={colors.whatsappGreen} />;
            case 'EMAIL':
                // @ts-expect-error
                return <NavIconEmailIcon size={30} fill={colors.orange} />;
            default:
                return null;
        }
    }

    return null;
}

// Gets the htmlUrl for opening this notification. Some entities load in a modal
// now, but our backend doesn't support this yet so we need to manually add the
// # sign here instead of from the server.
export function getHtmlUrl(notification: NotificationObject) {
    const entity = notification.entity;

    if (entity && entity.__typename === 'Activity') {
        if (NutClientConfig.hasNewActivityModalFF) {
            return {
                url: `/${entity.id}`,
                useHash: true,
                navigationType: 'push',
            };
        } else {
            return {
                url: `/activity/view/${entity.id}`,
                useHash: false,
                navigationType: 'push',
            };
        }
    } else if (
        notification.destinationEntity &&
        notification.destinationEntity.__typename === 'Activity'
    ) {
        if (NutClientConfig.hasNewActivityModalFF) {
            return {
                url: `/${notification.destinationEntity.id}`,
                useHash: true,
                navigationType: 'push',
            };
        } else {
            return {
                url: `/activity/view/${notification.destinationEntity.id}`,
                useHash: false,
                navigationType: 'push',
            };
        }
    } else if (
        notification.type === 'MENTION' ||
        notification.type === 'REACTION' ||
        notification.type === 'COMMENT' ||
        notification.type === 'QUOTE_VIEWED' ||
        notification.type === 'QUOTE_ACCEPTED' ||
        notification.type === 'INVOICE_VIEWED'
    ) {
        return notification.htmlUrl
            ? {
                  url: notification.htmlUrl,
                  useHash: notification.destinationEntity?.__typename !== 'Thread',
                  navigationType: 'push',
              }
            : null;
    } else if (
        (entity && entity.__typename === 'Message') ||
        notification.type === 'THREAD_ASSIGNMENT' ||
        notification.type === 'THREAD_UNASSIGNED_START'
    ) {
        return notification.htmlUrl
            ? {url: notification.htmlUrl, useHash: false, navigationType: 'reload'}
            : null;
    }

    return notification.htmlUrl
        ? {url: notification.htmlUrl, useHash: false, navigationType: 'push'}
        : null;
}

export function shouldOpenActivityModal(notification: NotificationObject) {
    if (NutClientConfig.hasNewActivityModalFF) {
        return false;
    }

    const entity = notification.entity;

    return (
        (notification.destinationEntity &&
            notification.destinationEntity.__typename === 'Activity') ||
        (entity ? entity.__typename === 'Activity' : false)
    );
}
